<script setup lang="ts">
import DownloadDocButton from "@/components/buttons/DownloadDocButton.vue";
import { ActivationItemGroup } from "@/types/calculators";
import { computed, defineProps, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  FileChild,
  IParagraphStyleOptions,
  AlignmentType,
  PageBreak,
  VerticalAlign,
} from "docx";
import { saveAs } from "file-saver";
import { sectors } from "@/constants/activationSectors";

const props = defineProps<{
  activations: ActivationItemGroup;
  activationKey: string;
}>();

const { t, tm } = useI18n();

type ActivatorItem = {
  title: string;
  items: string[];
};

type FAQTemplate = {
  type: "title" | "subtitle" | "paragraph" | "list";
  content: ContentItem | ContentItem[];
};

type ContentItem =
  | string
  | {
      type: "bold" | "text";
      text: string;
    };

const generatingDoc = ref(false);

const activationFAQ = computed(
  () => tm(`faq.activations.${props.activationKey}`) as FAQTemplate[]
);
const activatorsFAQ = computed(() => tm("faq.activators") as ActivatorItem[]);

const getParagraphStyles = (): IParagraphStyleOptions[] => {
  return [
    {
      id: "DocTitle",
      name: "Activation Title Style",
      run: {
        font: "Aptos",
        bold: true,
        size: 64, // 32pt
      },
      paragraph: {
        spacing: { after: 720 },
        alignment: AlignmentType.CENTER,
      },
    },
    {
      id: "SectionTitle",
      name: "SectionTitle",
      run: {
        font: "Aptos",
        bold: true,
        size: 40, // 20pt
      },
      paragraph: {
        spacing: { before: 480, after: 240 },
        alignment: AlignmentType.CENTER,
      },
    },
    {
      id: "SectionSubTitle",
      name: "SectionSubTitle",
      run: {
        font: "Aptos",
        bold: true,
        size: 32, // 16pt
      },
      paragraph: {
        spacing: { before: 360, after: 160 },
        alignment: AlignmentType.LEFT,
      },
    },
    {
      id: "MainText",
      name: "MainText",
      run: {
        font: "Georgia",
        bold: false,
        size: 24, // 12pt
      },
      paragraph: {
        alignment: AlignmentType.JUSTIFIED,
        spacing: {
          line: 312,
          lineRule: "auto",
        },
        indent: {
          firstLine: "1.25cm",
        },
      },
    },
    {
      id: "List",
      name: "List",
      run: {
        font: "Georgia",
        bold: false,
        size: 24, // 12pt
      },
      paragraph: {
        alignment: AlignmentType.LEFT,
        spacing: {
          line: 288,
          lineRule: "auto",
          after: 240,
        },
        indent: {
          left: "0.63cm",
        },
        contextualSpacing: true,
      },
    },
    {
      id: "TableHeader",
      name: "TableHeader",
      basedOn: "MainText",
      run: {
        font: "Aptos",
        bold: true,
      },
      paragraph: {
        alignment: AlignmentType.CENTER,
        spacing: {
          line: 240,
          before: 120,
          after: 120,
        },
        indent: {
          firstLine: 0,
        },
      },
    },
    {
      id: "TableContent",
      name: "TableContent",
      basedOn: "MainText",
      run: {
        font: "Aptos",
      },
      paragraph: {
        alignment: AlignmentType.CENTER,
        spacing: {
          line: 240,
          before: 80,
          after: 80,
        },
        indent: {
          firstLine: 0,
        },
      },
    },
  ];
};

function addPageBreaker(children: FileChild[]) {
  children.push(
    new Paragraph({
      children: [new PageBreak()],
    })
  );
}

async function handleExportToDoc() {
  if (generatingDoc.value) return;

  generatingDoc.value = true;

  const children: FileChild[] = [];

  addTitleBlock(children);
  addActivationFAQBlock(children);
  addPageBreaker(children);

  addActivatorsFAQBlock(children);
  addPageBreaker(children);

  addSectorsTable(children);
  addPageBreaker(children);

  addActivationsTable(children);

  const doc = new Document({
    styles: {
      paragraphStyles: getParagraphStyles(),
    },
    sections: [{ children }],
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, "activationsResults.docx");

  setTimeout(() => (generatingDoc.value = false), 500);
}

function addTitleBlock(children: FileChild[]) {
  children.push(
    new Paragraph({
      text: t("calculators.activations.title"),
      style: "DocTitle",
    })
  );

  children.push(new Paragraph({ text: "FAQ", style: "SectionTitle" }));
}

function addActivationFAQBlock(children: FileChild[]) {
  children.push(
    new Paragraph({
      text: t(`calculators.activations.group.${props.activationKey}`),
      style: "SectionSubTitle",
    })
  );
  activationFAQ.value.forEach((item: FAQTemplate) => {
    switch (item.type) {
      case "paragraph":
        children.push(createParagraphFAQItem(item));
        break;
      case "subtitle":
      case "title":
        children.push(createTitleFAQItem(item));
        break;
      case "list":
        children.push(...createListFAQItem(item));
    }
  });
}

function addActivatorsFAQBlock(children: FileChild[]) {
  children.push(
    new Paragraph({
      text: t("calculators.activations.faq.activators.title"),
      style: "SectionTitle",
    })
  );
  activatorsFAQ.value.forEach((item: ActivatorItem) => {
    children.push(
      new Paragraph({ text: item.title, style: "SectionSubTitle" })
    );
    item.items.forEach((listItem) => {
      children.push(
        new Paragraph({ text: listItem, bullet: { level: 0 }, style: "List" })
      );
    });
  });
}

function addSectorsTable(children: FileChild[]) {
  children.push(
    new Paragraph({ text: t("faq.sectors.title"), style: "SectionTitle" })
  );
  let directionIndex = 0;
  let isOdd = false;
  const rows = sectors.map((sector) => {
    let rowCells: TableCell[] = [];
    if (sector.direction !== undefined) {
      isOdd = ++directionIndex % 2 === 0;
      rowCells.push(
        new TableCell({
          rowSpan: 3,
          verticalAlign: VerticalAlign.CENTER,
          shading: isOdd ? { fill: "F2F2F2" } : undefined,
          children: [
            new Paragraph({
              text: t(sector.direction as string),
              style: "TableContent",
            }),
          ],
        })
      );
    }
    const cellValues = [
      sector.degree,
      t(sector.mountain),
      t(sector.name),
      sector.hieroglyph,
    ];
    cellValues.forEach((cellValue) => {
      rowCells.push(
        new TableCell({
          children: [new Paragraph({ text: cellValue, style: "TableContent" })],
          shading: isOdd ? { fill: "F2F2F2" } : undefined,
          verticalAlign: VerticalAlign.CENTER,
        })
      );
    });

    return new TableRow({
      children: rowCells,
    });
  });

  const tableHeaderKeys = [
    "faq.sectors.headers.direction",
    "faq.sectors.headers.compas-direction",
    "faq.sectors.headers.mountain",
    "faq.sectors.headers.name",
    "faq.sectors.headers.hieroglyph",
  ];
  const tableHeader = tableHeaderKeys.map(
    (key) =>
      new TableCell({
        children: [new Paragraph({ text: t(key), style: "TableHeader" })],
        shading: { fill: "F2F2F2" },
      })
  );

  children.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: tableHeader,
        }),
        ...rows,
      ],
    })
  );
}

function addActivationsTable(children: FileChild[]) {
  children.push(
    new Paragraph({
      text: t("calculators.activations.results.title"),
      style: "SectionTitle",
    })
  );

  children.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [getActivationsTableHeader(), ...getActivationsTableContent()],
    })
  );
}

function getActivationsTableHeader() {
  const headerKeys = [
    "calculators.activations.results.name",
    "calculators.activations.results.date",
    "calculators.activations.results.time",
    "calculators.activations.results.sector",
    "calculators.activations.results.activators",
    "calculators.activations.results.not_suitable_for",
  ];
  const headerCells = headerKeys.map((key) => {
    return new TableCell({
      shading: { fill: "F2F2F2" },
      children: [
        new Paragraph({
          text: t(key),
          style: "TableHeader",
        }),
      ],
    });
  });

  return new TableRow({
    children: headerCells,
  });
}

function getActivationsTableContent() {
  return props.activations.map((item) => {
    let sectors = [];
    if (Array.isArray(item.activation_sector)) {
      for (const sector of item.activation_sector) {
        sectors.push(
          new Paragraph({
            text: t(`faq.sectors.mountains.${sector.sector}`),
            style: "TableContent",
          })
        );
      }
    } else {
      sectors = [
        new Paragraph({
          text: t(`faq.sectors.mountains.${item.activation_sector}`),
          style: "TableContent",
        }),
      ];
    }

    return new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              text: t(
                `calculators.activations.activation_list.${item.activation_name}`
              ),
              style: "TableContent",
            }),
          ],
        }),
        new TableCell({
          children: [new Paragraph({ text: item.date, style: "TableContent" })],
        }),
        new TableCell({
          children: [
            new Paragraph({
              text: item.hour_symbol + " " + item.time_interval,
              style: "TableContent",
            }),
          ],
        }),
        new TableCell({
          children: sectors,
        }),
        new TableCell({
          children: [
            new Paragraph({
              text: item.activators
                .map((activator) => {
                  return t(`calculators.activations.activators.${activator}`);
                })
                .join(", "),
              style: "TableContent",
            }),
          ],
        }),
        new TableCell({
          children: [
            new Paragraph({
              text: item.not_suitable_for,
              style: "TableContent",
            }),
          ],
        }),
      ],
    });
  });
}

function createListFAQItem(item: FAQTemplate): Paragraph[] {
  const contentItems = Array.isArray(item.content)
    ? item.content
    : [item.content];
  const listItems: Paragraph[] = [];

  contentItems.forEach((contentItem) => {
    if (typeof contentItem === "string") {
      listItems.push(
        new Paragraph({
          text: contentItem,
          bullet: { level: 0 },
          style: "List",
        })
      );
    } else if (Array.isArray(contentItem)) {
      const runs = contentItem.map((subPart: ContentItem) => {
        return new TextRun({
          text: typeof subPart === "string" ? subPart : subPart.text,
          style: "MainText",
        });
      });
      listItems.push(
        new Paragraph({
          children: runs,
          bullet: { level: 0 },
          style: "List",
        })
      );
    }
  });

  return listItems;
}

function createTitleFAQItem(item: FAQTemplate): Paragraph {
  return new Paragraph({
    text: item.content as string,
    style: "SectionSubTitle",
  });
}

function createParagraphFAQItem(item: FAQTemplate): Paragraph {
  const content = item.content;
  if (!Array.isArray(content)) {
    if (typeof content === "string") {
      return new Paragraph({
        text: content,
        style: "MainText",
      });
    }
    return new Paragraph({
      text: content.text,
      style: "MainText",
    });
  }

  let textParts = "";
  content.forEach((contentItem: ContentItem) => {
    if (typeof contentItem === "string") {
      textParts += contentItem;
    } else {
      textParts += contentItem.text;
    }
  });

  return new Paragraph({
    text: textParts,
    style: "MainText",
  });
}
</script>

<template>
  <DownloadDocButton
    btn-classes="size-11"
    icon-classes="size-6 ml-1"
    @click="handleExportToDoc"
  />
</template>
